import { cn } from "@/lib/utils";
import { forwardRef } from "react";
import { EditCustomLabelDialog } from "@/components/CustomLabels/EditCustomLabelDialog";
import { AvailableLabel, Integrators, IntegratorsLabel } from "@/types/v2";
import { Icon } from "@/components/Common/Icon";
import { Tooltip } from "@/components/Common/Tooltip";
import { isLabelSyncedFromCatalog } from "@/lib/labelHasCatalogMatch";

export interface CustomLabelItemProps {
  label: string;
  onEdit?: (oldLabel: string, newLabel: string) => Promise<void>; // Callback for the edit button
  onDelete?: (label: string) => Promise<void>; // Callback for the delete button
  integration?: Integrators;
  isSynced?: boolean; // Whether this label matches a live entry in the shop's label catalog
}

export interface CustomLabelListProps {
  labels: string[]; // List of custom labels
  className?: string; // Optional additional classes
  onEdit?: (oldLabel: string, newLabel: string) => Promise<void>; // Callback for the edit button
  onDelete?: (label: string) => Promise<void>; // Callback for the delete button
  integration?: Integrators;
  labelCatalog?: AvailableLabel[]; // The shop's real Tekmetric label catalog, for gating synced labels
}

const CustomLabelItem = forwardRef<HTMLDivElement, CustomLabelItemProps>(
  ({ label, onEdit, onDelete, integration, isSynced }, ref) => (
    <div
      ref={ref}
      className="flex items-center justify-between rounded-md px-4 py-1"
    >
      <div className="flex items-center space-x-4">
        <div className="flex h-[40px] w-[150px] items-center justify-center rounded-sm bg-primary">
          {label && (
            <span className={cn("text-center text-sm font-medium text-white")}>
              {label}
            </span>
          )}
        </div>
        {isSynced && (
          <Tooltip
            message={`Synced from ${
              integration
                ? IntegratorsLabel[integration]
                : "your shop management system"
            }'s label catalog`}
          >
            <span className="inline-flex">
              <Icon name="sync" className="h-4 w-4 text-muted-foreground" />
            </span>
          </Tooltip>
        )}
      </div>
      <EditCustomLabelDialog
        label={label}
        onSave={(oldLabel, newLabel) => {
          if (onEdit) {
            onEdit(oldLabel, newLabel);
          }
        }}
        onDelete={() => {
          if (onDelete) {
            onDelete(label);
          }
        }}
        integration={integration}
        isSynced={isSynced}
      />
    </div>
  )
);
CustomLabelItem.displayName = "CustomLabelItem";

const CustomLabelList = forwardRef<HTMLDivElement, CustomLabelListProps>(
  ({ labels, className, onEdit, onDelete, integration, labelCatalog }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-2", className)}>
      {labels.length === 0 ? (
        <div className="text-center text-muted-foreground">
          No labels found. Labels will appear here when created or added during
          a sync with {integration ? IntegratorsLabel[integration] : "UNKNOWN"}.
        </div>
      ) : (
        labels.map((label) => (
          <CustomLabelItem
            key={label}
            label={label}
            onEdit={onEdit}
            onDelete={onDelete}
            integration={integration}
            isSynced={isLabelSyncedFromCatalog(label, labelCatalog)}
          />
        ))
      )}
    </div>
  )
);
CustomLabelList.displayName = "CustomLabelList";

export { CustomLabelList, CustomLabelItem };
